import { useEffect, useRef, useState } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import redDotImg from 'public/images/red_dot.png';
import ClientOnlyComponent from 'src/components/ClientOnlyComponent'
import { toJpeg } from 'html-to-image'
import { TransportItem } from 'src/types';
import ImageWithFallback from '../ImageWithFallback';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import ApiClient from 'src/lib/api_client';
import { Modal } from 'react-bootstrap'
import { FiX } from 'react-icons/fi'
import useBreakpoints from 'src/hooks/useBreakpoints';

type MapProps = {
  location: { latitude: number; longitude: number };
  transportItem: TransportItem;
};

const MapInner: React.FC<MapProps> = ({ location, transportItem }) => {
  const mapEleRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map>();
  const markerRef = useRef<google.maps.Marker>();
  const shouldSaveMapAsImage = transportItem.freshMapImageCacheKey !== transportItem.mapImageCacheKey
  const uploadMapImage = useEnhanceApiClient(ApiClient.uploadMapImage)
  const { isScreenMd } = useBreakpoints()

  useEffect(() => {
    if (mapEleRef.current && mapRef.current) {
      markerRef.current?.setMap(null);
      mapRef.current.setCenter({
        lat: location.latitude,
        lng: location.longitude,
      });
    }
  }, [location.latitude, location.longitude]);

  useEffect(() => {
    if (mapEleRef.current) {
      mapRef.current = new window.google.maps.Map(mapEleRef.current, {
        center: { lat: location.latitude, lng: location.longitude },
        zoom: 15,
        gestureHandling: 'greedy',
        scrollwheel: false,
        clickableIcons: false,
        mapTypeControl: false,
        fullscreenControl: false,
        rotateControl: false,
        streetViewControl: false,
      });

      markerRef.current = new window.google.maps.Marker({
        position: { lat: location.latitude, lng: location.longitude },
        map: mapRef.current,
        icon: redDotImg.src,
      });

      if (shouldSaveMapAsImage) {
        window.google.maps.event.addListenerOnce(mapRef.current, 'tilesloaded', () => {
          if (mapEleRef.current && isScreenMd) {
            toJpeg(mapEleRef.current).then((data) => {
              uploadMapImage({
                file: data,
                record: `TransportItem_${transportItem.id}`
              })
            })
          }
        })
      }
    }
  }, []);

  return <div ref={mapEleRef} className="tw-h-full tw-rounded-md"></div>;
};

const Map: React.FC<MapProps> = (props) => {
  const [showModal, setShowModal] = useState(false)
  const shouldSaveMapAsImage = props.transportItem.freshMapImageCacheKey !== props.transportItem.mapImageCacheKey

  return (
    <ClientOnlyComponent>
      <>
        {shouldSaveMapAsImage ? (
          <Wrapper apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY as string}>
            <MapInner {...props} />
          </Wrapper>
        ) : props.transportItem.mapImage ? (
          <ImageWithFallback
            record={props.transportItem.mapImage}
            imgField="webpXlUrl"
            alt=""
            fill
            className="tw-block tw-static tw-object-cover tw-h-full tw-w-full tw-rounded-md"
            onClick={() => {
              setShowModal(true)
            }}
          />
        ) : (
          <Wrapper apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY as string}>
            <MapInner {...props} />
          </Wrapper>
        )}

        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false)
          }}
          className="fade"
          dialogClassName="modal-dialog-centered"
        >
          <Modal.Header>
            <div></div>
            <span className="tw-cursor-pointer" onClick={() => setShowModal(false)}>
              <FiX size={24} strokeWidth={1} className="tw-text-brand-navy" />
            </span>
          </Modal.Header>
          <Modal.Body>
            <div className="tw-h-80 md:tw-h-96 tw-border-2 tw-border-brand-green tw-rounded-md">
              <Wrapper apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY as string}>
                <MapInner {...props} />
              </Wrapper>
            </div>
          </Modal.Body>
        </Modal>
      </>
    </ClientOnlyComponent>
  );
};

export default Map;
